import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatTL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { useFavorites } from "@/contexts/FavoritesContext";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";

export default function Account() {
  const { user, logout } = useAuth();
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "profile";
  const navigate = useNavigate();

  const tabs = [
    { key: "profile", label: t("nav.account") },
    { key: "orders", label: lang === "tr" ? "Siparişlerim" : "Orders" },
    { key: "addresses", label: lang === "tr" ? "Adreslerim" : "Addresses" },
    { key: "favorites", label: t("nav.favorites") },
  ];

  return (
    <div data-testid="account-page" className="px-6 lg:px-16 py-12 max-w-[1440px] mx-auto">
      <h1 className="font-heading text-4xl lg:text-5xl mb-10">{lang === "tr" ? "Hesabım" : "My Account"}</h1>
      <div className="grid lg:grid-cols-[240px_1fr] gap-12">
        <aside className="lg:sticky lg:top-32 lg:self-start space-y-2">
          {tabs.map((tt) => (
            <button key={tt.key} data-testid={`tab-${tt.key}`}
              onClick={() => setParams({ tab: tt.key })}
              className={`block w-full text-left text-sm py-2 border-l-2 pl-4 transition-colors ${tab === tt.key ? "border-[#C8A96A] text-[#C8A96A]" : "border-transparent text-[#52525B] hover:text-[#0A0A0A]"}`}>
              {tt.label}
            </button>
          ))}
          <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }}
            className="block w-full text-left text-sm py-2 border-l-2 border-transparent pl-4 text-[#EF4444] hover:opacity-80 mt-6">
            {t("nav.logout")}
          </button>
        </aside>
        <div>
          {tab === "profile" && <Profile user={user} />}
          {tab === "orders" && <Orders />}
          {tab === "addresses" && <Addresses />}
          {tab === "favorites" && <Favorites />}
        </div>
      </div>
    </div>
  );
}

function Profile({ user }) {
  const { lang } = useLang();
  return (
    <div data-testid="profile-section" className="space-y-4">
      <h2 className="font-heading text-2xl mb-4">{lang === "tr" ? "Profil Bilgileri" : "Profile"}</h2>
      <div className="bg-[#F5F2EC] p-6 space-y-3 text-sm">
        <div><span className="text-[#52525B]">{lang === "tr" ? "Ad" : "Name"}: </span>{user?.name}</div>
        <div><span className="text-[#52525B]">Email: </span>{user?.email}</div>
        <div><span className="text-[#52525B]">{lang === "tr" ? "Rol" : "Role"}: </span><span className="uppercase tracking-wider text-[#C8A96A]">{user?.role}</span></div>
      </div>
    </div>
  );
}

function Orders() {
  const { lang } = useLang();
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)); }, []);
  return (
    <div data-testid="orders-section">
      <h2 className="font-heading text-2xl mb-4">{lang === "tr" ? "Siparişlerim" : "Orders"}</h2>
      {orders.length === 0 ? (
        <p className="text-sm text-[#52525B]">{lang === "tr" ? "Henüz siparişiniz yok." : "No orders yet."}</p>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="border border-border p-5">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-finance text-xs text-[#52525B]">#{o.id.slice(0, 8)}</p>
                  <p className="text-xs text-[#52525B]">{new Date(o.created_at).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US")}</p>
                </div>
                <span className="text-xs uppercase tracking-wider px-3 py-1 bg-[#0A0A0A] text-white">{o.status}</span>
              </div>
              <div className="text-sm space-y-1">
                {o.items?.map((it, idx) => <div key={idx} className="text-[#52525B]">× {it.quantity} — {formatTL(it.price)}</div>)}
              </div>
              <div className="mt-3 pt-3 border-t border-border flex justify-between">
                <span className="text-sm">{lang === "tr" ? "Toplam" : "Total"}</span>
                <span className="font-finance">{formatTL(o.total)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Addresses() {
  const { lang } = useLang();
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: "", full_name: "", phone: "", address: "", city: "", district: "", postal_code: "" });
  const reload = () => api.get("/addresses").then((r) => setList(r.data));
  useEffect(() => { reload(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/addresses", form);
    setForm({ title: "", full_name: "", phone: "", address: "", city: "", district: "", postal_code: "" });
    reload();
    toast.success(lang === "tr" ? "Adres eklendi" : "Address added");
  };
  return (
    <div data-testid="addresses-section">
      <h2 className="font-heading text-2xl mb-4">{lang === "tr" ? "Adreslerim" : "Addresses"}</h2>
      <div className="space-y-3 mb-8">
        {list.map((a) => (
          <div key={a.id} className="border border-border p-4 flex justify-between text-sm">
            <div>
              <p className="font-medium">{a.title}</p>
              <p className="text-[#52525B]">{a.full_name} — {a.phone}</p>
              <p className="text-[#52525B]">{a.address}, {a.district}, {a.city} {a.postal_code}</p>
            </div>
            <button onClick={async () => { await api.delete(`/addresses/${a.id}`); reload(); }} className="text-[#EF4444] text-xs">{lang === "tr" ? "Sil" : "Delete"}</button>
          </div>
        ))}
      </div>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 bg-[#F5F2EC] p-6">
        {["title", "full_name", "phone", "city", "district", "postal_code"].map((k) => (
          <input key={k} required={k !== "postal_code"} placeholder={k} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            className="border-b border-[#0A0A0A] bg-transparent py-2 text-sm focus:outline-none" />
        ))}
        <input required placeholder="address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="sm:col-span-2 border-b border-[#0A0A0A] bg-transparent py-2 text-sm focus:outline-none" />
        <button className="sm:col-span-2 bg-[#0A0A0A] hover:bg-[#C8A96A] text-white py-3 text-xs uppercase tracking-[0.25em] transition-colors">
          {lang === "tr" ? "Adres Ekle" : "Add Address"}
        </button>
      </form>
    </div>
  );
}

function Favorites() {
  const { lang } = useLang();
  const { ids, ready } = useFavorites();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!ready) return;
    if (ids.length === 0) { setList([]); setLoading(false); return; }
    setLoading(true);
    api.get("/favorites").then((r) => setList(r.data || [])).finally(() => setLoading(false));
  }, [ids, ready]);
  return (
    <div data-testid="favorites-section">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-heading text-2xl">{lang === "tr" ? "Favorilerim" : "Favorites"}</h2>
        <Link to="/favorites" className="text-xs uppercase tracking-[0.25em] underline underline-offset-8 decoration-[#C8A96A]">
          {lang === "tr" ? "Tümünü görüntüle" : "View all"}
        </Link>
      </div>
      {loading ? (
        <p className="text-sm text-[#52525B]">{lang === "tr" ? "Yükleniyor..." : "Loading..."}</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-[#52525B]">{lang === "tr" ? "Henüz favoriniz yok." : "No favorites yet."}</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {list.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
}
